import React from 'react'

// @mui material components
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'

// @mui icons
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Material Dashboard 2 PRO React examples
import Footer from 'examples/Footer'
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import { useNavigate } from 'react-router-dom'

const ONBOARDING_CARDS = [
    {
        key: 'customer',
        title: 'New Customer',
        description: 'Create a new customer entity complete with service address, contacts, tanks/assets, and device assignment — all in one guided workflow.',
        icon: <PersonOutlineIcon fontSize="medium" />,
        href: '/onboarding/customer'
    },
    {
        key: 'address',
        title: 'Add Address',
        description: 'Add a new service address to an existing customer or entity. Search for the entity, fill in the address, and save.',
        icon: <HomeOutlinedIcon fontSize="medium" />,
        href: '/onboarding/add-address'
    },
    {
        key: 'contacts',
        title: 'Add Contacts',
        description: 'Add new contacts to an existing entity. Search for the customer, fill in contact details, and link them to the entity.',
        icon: <ContactsOutlinedIcon fontSize="medium" />,
        href: '/onboarding/add-contacts'
    },
    {
        key: 'items',
        title: 'Add Items / Assets',
        description: 'Add new tanks or other assets to an existing address. Select the customer and address, then configure the items.',
        icon: <Inventory2OutlinedIcon fontSize="medium" />,
        href: '/onboarding/add-items'
    },
    {
        key: 'assign-device',
        title: 'Assign Device',
        description: 'Assign a monitoring device to an existing address item. Select the customer, pick the asset, then assign a device from inventory.',
        icon: <SensorsOutlinedIcon fontSize="medium" />,
        href: '/onboarding/assign-device'
    },
    {
        key: 'notifications',
        title: 'Notifications',
        description: 'Set up or edit SMS and email alert rules for a customer\'s device. Configure high level, low level, and other event alerts per contact.',
        icon: <NotificationsNoneOutlinedIcon fontSize="medium" />,
        href: '/onboarding/notification'
    },
    {
        key: 'device-claims',
        title: 'Device Claims',
        description: 'Track device installation claims: issued, expired, revoked, and completed installs, with installation summaries and setup follow-ups.',
        icon: <QrCodeScannerOutlinedIcon fontSize="medium" />,
        href: '/onboarding/device-claims'
    },
]

function OnboardingCardsContent() {
    const navigate = useNavigate()
    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            {ONBOARDING_CARDS.map((c) => (
                <Grid key={c.key} item xs={12} sm={6} lg={4}>
                    <Card
                        variant="outlined"
                        sx={{
                            height: '100%',
                            borderRadius: 2,
                        }}
                    >
                        <CardActionArea
                            sx={{ height: '100%', alignItems: 'stretch' }}
                            onClick={() => {
                                // Placeholder: hook navigation / stepper here later
                            }}
                        >
                            <CardContent sx={{ height: '100%' }} onClick={() => navigate(c.href)}>
                                <MDBox display="flex" alignItems="center" gap={1.25} mb={1}>
                                    <MDBox
                                        display="grid"
                                        placeItems="center"
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: 2,
                                            bgcolor: 'grey.100',
                                            color: 'text.primary',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {c.icon}
                                    </MDBox>
                                    <MDTypography variant="h6" fontWeight="medium">
                                        {c.title}
                                    </MDTypography>
                                </MDBox>
                                <MDTypography variant="body2" color="text" sx={{ opacity: 0.85 }}>
                                    {c.description}
                                </MDTypography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            ))}
        </Grid>
    )
}

function Onboarding() {
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={1}>
                <Card>
                    <MDBox p={3} lineHeight={1}>
                        <MDTypography variant="h5" fontWeight="medium">
                            Onboarding
                        </MDTypography>
                        <MDTypography variant="body2" color="text" sx={{ opacity: 0.8, mt: 1 }}>
                            Complete the steps below to onboard a new customer.
                        </MDTypography>
                    </MDBox>
                    <Divider />
                    <CardContent >
                        <OnboardingCardsContent />
                    </CardContent>
                </Card>
            </MDBox>
            <Footer />
        </DashboardLayout>
    )
}

export default Onboarding